// SolutionGuidedCalm.js
import React, { useState } from "react";
import { motion } from "framer-motion";

/**
 * 2025 Scrollytelling — Sticky River
 * - Left: sticky SVG "river" with glowing nodes
 * - Right: scroll steps (headline + one subline), no cards
 * - Each step activates a node + gentle pulse along the river
 */

const STEPS = [
  {
    key: "see-truth",
    title: "See the true state of your data.",
    sub: "Automatically profile systems across your business — CRM, ERP, finance, HR, operations — and surface duplicates, gaps, inconsistencies, and anomalies with clear quality scores.",
  },
  {
    key: "fix-issues",
    title: "Fix the issues before they hit the business.",
    sub: "Use AI-powered cleansing to standardise values, correct formats, deduplicate records, and enrich missing fields, with full control over what’s auto-applied and what needs review.",
  },
  {
    key: "stay-healthy",
    title: "Keep data healthy as you grow and transform.",
    sub: "Set up rules, alerts, and guardrails so new data meets your standards — supporting migrations, reporting, AI initiatives, and day-to-day operations without constant firefighting.",
  },
  {
    key: "put-to-work",
    title: "Put it to work — for people and for AI agents.",
    sub: "Analyse trends across datasets, ask the built-in assistant a question in plain English, or connect your own AI agents over MCP so they can query and reason over the same clean, governed data — with the same access controls as any user.",
  },
];

export default function SolutionGuidedCalm({
  title = "A calm path from messy data to trusted decisions.",
  subtitle = "From first profile to ongoing monitoring and analysis, PrismDQ gives every team a way to understand, clean, govern, and query their data — across any system, any industry, and now any AI agent.",
  steps = STEPS,
}) {
  const [active, setActive] = useState(0);

  return (
    <section id="solution" className="relative py-20">
      {/* ambient backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-64 w-[85vw] -translate-x-1/2 rounded-[8rem] bg-[radial-gradient(40rem_16rem_at_50%_0%,rgba(96,165,250,0.16),transparent)]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
          <p className="mt-3 text-slate-600">{subtitle}</p>
        </div>

        <div className="mt-12 grid grid-cols-12 gap-8">
          {/* Sticky River (left) */}
          <div className="relative col-span-12 lg:col-span-5">
            <div className="sticky top-28">
              <div className="relative h-[70vh] w-full overflow-visible">
                {/* River path */}
                <svg viewBox="0 0 320 560" className="h-[70vh] w-full">
                  <defs>
                    <linearGradient id="river" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.9" />
                    </linearGradient>
                    <linearGradient id="riverSoft" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="rgba(37,99,235,0.15)" />
                      <stop offset="100%" stopColor="rgba(34,211,238,0.15)" />
                    </linearGradient>
                    <filter id="blur">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="5" />
                    </filter>
                  </defs>

                  {/* soft halo */}
                  <path
                    d="M40 20 C 160 60, 120 140, 200 180 S 300 300, 160 360 140 460, 280 520"
                    stroke="url(#riverSoft)"
                    strokeWidth="18"
                    fill="none"
                    filter="url(#blur)"
                    opacity="0.9"
                  />
                  {/* main river */}
                  <path
                    d="M40 20 C 160 60, 120 140, 200 180 S 300 300, 160 360 140 460, 280 520"
                    stroke="url(#river)"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />

                  {/* Nodes along the river (positions roughly match steps) */}
                  {[
                    { cx: 64, cy: 36 },
                    { cx: 164, cy: 120 },
                    { cx: 210, cy: 180 },
                    { cx: 230, cy: 260 },
                    { cx: 170, cy: 360 },
                    { cx: 260, cy: 520 },
                  ].map((p, i) => (
                    <g key={i}>
                      <circle cx={p.cx} cy={p.cy} r="12" fill="rgba(37,99,235,0.12)" />
                      <motion.circle
                        cx={p.cx}
                        cy={p.cy}
                        r={active === i ? 8 : 5}
                        fill={active === i ? "#2563EB" : "#60A5FA"}
                        animate={{
                          scale: active === i ? [1, 1.12, 1] : 1,
                          opacity: active === i ? [1, 0.8, 1] : 0.9,
                        }}
                        transition={{ duration: 1.6, repeat: active === i ? Infinity : 0 }}
                      />
                    </g>
                  ))}
                </svg>

                {/* subtle moving sparkle along the path when active changes */}
                <motion.div
                  key={active}
                  className="pointer-events-none absolute left-0 top-0 h-full w-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.35 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="absolute inset-0 -z-10 bg-[radial-gradient(600px_160px_at_30%_20%,rgba(96,165,250,.18),transparent)]" />
                </motion.div>
              </div>
            </div>
          </div>

          {/* Narrative Steps (right) */}
          <div className="col-span-12 lg:col-span-7">
            <ul className="space-y-20">
              {steps.map((s, i) => (
                <li key={s.key}>
                  <motion.div
                    onViewportEnter={() => setActive(i)}
                    viewport={{ once: false, amount: 0.6 }}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="relative"
                  >
                    {/* Soft wash behind each scene (no box) */}
                    <div className="pointer-events-none absolute -inset-x-6 -top-6 -bottom-6 rounded-[4rem] bg-[radial-gradient(800px_200px_at_40%_-10%,rgba(96,165,250,.12),transparent)]" />
                    <h3 className="text-balance text-3xl sm:text-5xl font-semibold leading-[1.05] tracking-tight">
                      <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-800 bg-clip-text text-transparent">
                        {s.title}
                      </span>
                    </h3>
                    <p className="mt-3 max-w-2xl text-lg text-slate-600">
                      {s.sub}
                    </p>

                    {/* Tiny “how” line (kept but made universal) */}
                    <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
                      <span className="inline-block h-1 w-14 rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400" />
                      <span>Quietly handles the data work your teams shouldn’t be doing by hand.</span>
                    </div>
                  </motion.div>
                </li>
              ))}
            </ul>

            {/* Soft, non-salesy invitation */}
            <div className="mt-16 text-left">
              <a
                href="#features"
                className="inline-flex items-center rounded-2xl border border-slate-200 bg-white/70 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm backdrop-blur hover:bg-white"
              >
                See exactly how it works
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}